import Vue from 'vue';
import store from '@/store';
import i18n from '@/plugins/i18n';
import { helpers } from '@/common/helpers';
import { openSockets } from '@/common/sockets';
import { loadPillarCollectStript } from '@/common/pillarCollect';
import { setSentryUser } from '@/common/sentry';
import { set } from 'lodash';

export function initSockets() {
  openSockets();
  return store.dispatch('messenger/init');
}

export async function getAccountByToken(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/') || null;
  const accountInfo = JSON.parse(window.atob(base64));
  const userId = accountInfo.id;

  const response = await Vue.prototype.$api.accounts.getAccount(userId);
  store.commit('loggedAccount/setAccount', response.data.payload);
  setSentryUser(response.data.payload);
  return response.data.payload;
}

export async function loadWhiteLabel() {
  try {
    const response = await Vue.prototype.$api.whiteLabel.getWhiteLabel(window.location.host);
    store.commit('common/setWhitelabel', response.data.payload);

    const translate = response.data.payload?.translate;

    if (!translate) return;

    const translateKeys = Object.keys(translate);

    if (!translateKeys.length) return;

    const localization = i18n.getLocaleMessage(i18n.locale);

    translateKeys.forEach(key => {
      set(localization, key, translate[key][i18n.locale]);
    });

    i18n.setLocaleMessage(i18n.locale, localization);
  } catch (e) {
    console.log(e);
    store.commit('common/setWhitelabel', null);
  }
}
export async function loadBackendTranslations(language) {
  try {
    const translations = await Vue.prototype.$api.translations.getTranslationsLanguage(language);
    i18n.setLocaleMessage(i18n.locale, {
      ...i18n.getLocaleMessage(i18n.locale),
      backendTranslations: translations.data.payload,
    });
  } catch (e) {
    console.log(e);
  }
}

export async function loadSpecialitiesTranslations(language) {
  try {
    // get translations for provider specialities
    const providerSpecialities = await Vue.prototype.$api.translations.getTranslations('specialities', {
      lang: language,
    });
    i18n.setLocaleMessage(language, {
      ...i18n.getLocaleMessage(language),
      specialities: providerSpecialities.data.payload,
    });
  } catch (e) {
    console.log(e);
  }
}

export function fetchClinics() {
  const params = {
    pageSize: 2000,
    page: 0,
    sort: [{ name: 'id', dir: 'desc' }],
  };

  return Vue.prototype.$api.clinics.getClinics(params).then(response => store.commit('loggedAccount/setClinics', response.data.payload.results));
}

export function getClinicDoctors() {
  if (!Vue.prototype.$accountHasPermission('view:providers')) return;

  const params = {
    role: 'PROVIDER',
    pageSize: 2000,
  };

  return Vue.prototype.$api.accounts.getAccounts(params).then(({ data }) => {
    store.commit('loggedAccount/setDoctors', data.payload.results);
  });
}

export function getClinicAdmins() {
  const params = {
    role: 'CLINIC_ADMIN',
    pageSize: 2000,
  };

  return Vue.prototype.$api.accounts.getAccounts(params).then(({ data }) => {
    store.commit('loggedAccount/setClinicAdmins', data.payload.results);
  });
}

export async function getCurrencies() {
  const response = await Vue.prototype.$api.clinics.getCurrencies();
  store.commit('loggedAccount/setClinicCurrencies', response.data.payload);
}

export async function getClinicDetails(clinicIds) {
  const { data } = await Vue.prototype.$api.clinics.getClinics({ pageSize: 2000, page: 0, clinic: clinicIds });
  store.commit('loggedAccount/setClinics', data.payload.results);
  // TODO: ask backand to send fee details with $api.clinics.getClinics request
  // store.commit('loggedAccount/setAccountClinic', data.payload.results[0]);

  // TODO: delete next 2 lines after back-end will fix getClinics request
  if (clinicIds?.[0]) {
    const accountClinicResponse = await Vue.prototype.$api.clinics.getClinicById(clinicIds[0]);
    store.commit('loggedAccount/setAccountClinic', accountClinicResponse.data.payload);
  }
}

export function setAccountTimezone() {
  const timezone = helpers.timezoneForBackend(Vue.prototype.$timezone);
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return Vue.prototype.$api.timezones.setAccountTimezone({ timezone, timezoneName });
}

export async function loadCountries() {
  const savedCountries = JSON.parse(localStorage.getItem('phoneCodes'));
  console.log('savedCountries', savedCountries);
  const theSameVersion = store.state.common?.versions?.countriesPhones && savedCountries?.version && store.state.common.versions.countriesPhones === savedCountries.version;

  console.log('store.state.common?.versions?.countriesPhones', store.state.common?.versions?.countriesPhones);
  console.log('savedCountries?.version', savedCountries?.version);
  console.log('theSameVersion', theSameVersion);
  console.log('!savedCountries || !theSameVersion', !savedCountries || !theSameVersion);

  if (!savedCountries || !theSameVersion) {
    const response = await Vue.prototype.$api.countries.getCountries();

    savePhoneCodes(response.data.payload);
    saveCountries(response.data.payload);
  }
}

export async function loadLanguages() {
  const allLanguages = JSON.parse(localStorage.getItem('allLanguages'));
  console.log('allLanguages', allLanguages);
  const theSameVersions = store.state.common?.versions?.allLanguages && allLanguages?.version && store.state.common.versions.allLanguages === allLanguages.version;

  console.log('store.state.common?.versions?.allLanguages', store.state.common?.versions?.allLanguages);
  console.log('allLanguages?.version', allLanguages?.version);
  console.log('!theSameVersions', !theSameVersions);
  console.log('!allLanguages || !theSameVersions', !allLanguages || !theSameVersions);

  if (!allLanguages || !theSameVersions) {
    const response = await Vue.prototype.$api.common.getAllLanguages();
    saveLanguages(response.data.payload);

    return response.data.payload;
  }
}

function savePhoneCodes(countries) {
  const codes = countries.map(countryPhoneCode => ({
    value: countryPhoneCode.phoneCode,
    text: `${countryPhoneCode.name} (+${countryPhoneCode.phoneCode})`,
    id: countryPhoneCode.id,
    code: countryPhoneCode.code,
    name: countryPhoneCode.name,
  }));

  const phoneCodes = {
    version: store.state.common.versions.countriesPhones,
    value: codes,
  };

  localStorage.setItem('phoneCodes', JSON.stringify(phoneCodes));
}

function saveCountries(countries) {
  const filteredCountries = countries.map(country => ({
    code: country.code,
    value: country.name,
    text: country.name,
    id: country.id,
  }));

  const countriesData = {
    version: store.state.common.versions.countriesPhones,
    value: filteredCountries,
  };

  localStorage.setItem('countries', JSON.stringify(countriesData));
}

function saveLanguages(languages) {
  const languageData = {
    version: store.state.common.versions.allLanguages,
    value: languages,
  };

  localStorage.setItem('allLanguages', JSON.stringify(languageData));
}

export function showAppElements(show = true) {
  store.commit('common/setHideToolbar', !show);
  store.commit('common/setHideSidebar', !show);
}

export async function setPillarCollect() {
  const accountClinic = store.state?.loggedAccount?.accountClinic;
  const paymentType = accountClinic?.feeDetails?.type;

  if (paymentType === 'Pillar') {
    // const pillarCollectKey = accountClinic.feeDetails.tokenizationKey;
    const pillarCollectKey = '64Q6tB-a3PH66-p8sZ83-E96pj6';
    await loadPillarCollectStript(pillarCollectKey);
  }
}
